import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getExamSessions,
  createExamSession,
  updateExamSession,
  deleteExamSession,
  closeExamSession,
  openExamSession,
  cancelExamSession,
  getSessionRooms,
  assignRoomToSession,
  unassignRoomFromSession,
  downloadAttendanceSheet,
} from "../../../services/ExamSessionService";
import { getExams } from "../../../services/ExamService";
import { getClasses } from "../../../services/ClassService";
import { getStudentGroups } from "../../../services/StudentGroupService";
import { getRooms } from "../../../services/RoomService";
import DataTablePagination from "../common/DataTablePagination";
import { useTranslation } from "react-i18next";
import { usePermissions } from "../../../utils/permissions";
import { getLocale, lazyLabels } from "../../../utils/i18nLabels";
import { exportAttendanceExcel } from "../../../utils/exportAttendanceExcel";
import i18n from "../../../i18n";

const statusLabels = lazyLabels({ scheduled: "common.examSessionStatus.scheduled", ongoing: "common.examSessionStatus.ongoing", closed: "common.examSessionStatus.closed", cancelled: "common.examSessionStatus.cancelled" });
const statusBadge = { scheduled: "info", ongoing: "primary", closed: "success", cancelled: "danger" };

const ENSSS_TIME_ZONE = "Africa/Nouakchott";

const toLocalInput = (iso) =>
  iso ? new Date(iso).toISOString().slice(0, 16) : "";

const mauritaniaInputToISOString = (value) =>
  value ? new Date(`${value}:00.000Z`).toISOString() : null;

const formatMauritaniaDateTime = (value) =>
  value
    ? new Date(value).toLocaleString(getLocale(), { timeZone: ENSSS_TIME_ZONE })
    : "-";

const emptyForm = {
  exam_id: "",
  class_id: "",
  student_group_id: "",
  start_at: "",
  end_at: "",
  access_code: "",
};

const ExamSessions = () => {
  const { t } = useTranslation();
  // Le démarrage réel d'une session est réservé au directeur des études /
  // responsable des examens (permission EXAM_SESSION_OPEN) - voir réponse
  // client "le démarrage doit être sous le contrôle de la direction des études".
  const can = usePermissions();
  const canOpenSession = can("EXAM_SESSION_OPEN");
  const canCreateSession = can("EXAM_SESSION_CREATE");
  const canUpdateSession = can("EXAM_SESSION_UPDATE");
  const canCloseSession = can("EXAM_SESSION_CLOSE");
  const canDeleteSession = can("EXAM_SESSION_DELETE");

  // Salles affectées à une session (modal dédiée)
  const [roomsModalSession, setRoomsModalSession] = useState(null);
  const [sessionRooms, setSessionRooms] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [selectedRoomToAdd, setSelectedRoomToAdd] = useState("");
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomsSubmitting, setRoomsSubmitting] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [studentGroups, setStudentGroups] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [statusFilter, setStatusFilter] = useState("");
  const [examFilter, setExamFilter] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    getExams({ page: 1, limit: 200 }).then((res) => setExams(res.data?.data || [])).catch(() => { });
    getClasses({ page: 1, limit: 200 }).then((res) => setClasses(res.data?.data || [])).catch(() => { });
    getStudentGroups({ page: 1, limit: 200 }).then((res) => setStudentGroups(res.data?.data || [])).catch(() => { });
  }, []);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "start_at", sort_dir: "desc" };
      if (statusFilter) params.status = statusFilter;
      if (examFilter) params.exam_id = examFilter;

      const res = await getExamSessions(params);
      const body = res.data;
      setSessions(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.examSessions.impossibleChargerSessionsExamen"));
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, examFilter, t]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const label = (list, id) => list.find((x) => x.id === id)?.name || list.find((x) => x.id === id)?.title || "-";

  const openAddModal = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = (session) => {
    setFormMode("edit");
    setFormData({
      exam_id: session.exam_id || "",
      class_id: session.class_id || "",
      student_group_id: session.student_group_id || "",
      start_at: toLocalInput(session.start_at),
      end_at: toLocalInput(session.end_at),
      access_code: session.access_code || "",
    });
    setFormErrors({});
    setSubmitError("");
    setEditingId(session.id);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.exam_id) errors.exam_id = t("pages.examSessions.examRequired");
    if (!formData.start_at) errors.start_at = t("common.ui.laDateDebutEstRequise");
    if (!formData.end_at) errors.end_at = t("common.ui.laDateFinEstRequise");
    else if (formData.start_at && formData.end_at <= formData.start_at)
      errors.end_at = t("pages.examSessions.endAfterStart");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        exam_id: formData.exam_id,
        class_id: formData.class_id || null,
        student_group_id: formData.student_group_id || null,
        start_at: mauritaniaInputToISOString(formData.start_at),
        end_at: mauritaniaInputToISOString(formData.end_at),
        access_code: formData.access_code.trim() || null,
      };
      if (formMode === "add") {
        await createExamSession(payload);
        setFeedback({ type: "success", message: t("pages.examSessions.createSuccess") });
      } else {
        await updateExamSession(editingId, payload);
        setFeedback({ type: "success", message: t("pages.examSessions.updateSuccess") });
      }
      setShowFormModal(false);
      fetchSessions();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("common.crud.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteExamSession(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.examSessions.deleteSuccess") });
      setDeleteTarget(null);
      if (sessions.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchSessions();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.examSessions.deleteError") });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleOpen = async (session) => {
    try {
      await openExamSession(session.id);
      setFeedback({ type: "success", message: t("pages.examSessions.sessionDemarreeEtudiantsPeuventDesormaisY") });
      fetchSessions();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.examSessions.impossibleDemarrerSession") });
    }
  };

  const openRoomsModal = async (session) => {
    setRoomsModalSession(session);
    setSelectedRoomToAdd("");
    setRoomsLoading(true);
    try {
      const [roomsRes, allRoomsRes] = await Promise.all([
        getSessionRooms(session.id),
        allRooms.length ? Promise.resolve({ data: { data: allRooms } }) : getRooms({ page: 1, limit: 200, is_active: true }),
      ]);
      setSessionRooms(roomsRes.data?.data || []);
      if (allRoomsRes.data?.data) setAllRooms(allRoomsRes.data.data);
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.examSessions.impossibleChargerSallesSession") });
    } finally {
      setRoomsLoading(false);
    }
  };

  const handleAddRoom = async () => {
    if (!selectedRoomToAdd || !roomsModalSession) return;
    setRoomsSubmitting(true);
    try {
      await assignRoomToSession(roomsModalSession.id, selectedRoomToAdd);
      const res = await getSessionRooms(roomsModalSession.id);
      setSessionRooms(res.data?.data || []);
      setSelectedRoomToAdd("");
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.examSessions.impossibleAffecterSalle") });
    } finally {
      setRoomsSubmitting(false);
    }
  };

  const handleRemoveRoom = async (roomId) => {
    if (!roomsModalSession) return;
    try {
      await unassignRoomFromSession(roomsModalSession.id, roomId);
      setSessionRooms((prev) => prev.filter((r) => r.room_id !== roomId));
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.examSessions.impossibleRetirerSalle") });
    }
  };

  const handleDownloadAttendance = async (session) => {
    try {
      const res = await downloadAttendanceSheet(session.id);

      const data = res.data?.data;

      await exportAttendanceExcel({
        rows: data?.rows || [],
        session: data?.session || session,
        t,
        language: i18n.language,
      });

      setFeedback({
        type: "success",
        message: t(
          "pages.examSessions.emargementDownloadSuccess"
        ),
      });
    } catch (err) {
      setFeedback({
        type: "danger",
        message:
          err?.response?.data?.message ||
          t(
            "pages.examSessions.impossibleTelechargerListeEmargement"
          ),
      });
    }
  };

  const handleClose = async (session) => {
    try {
      await closeExamSession(session.id);
      setFeedback({ type: "success", message: t("pages.examSessions.closeSuccess") });
      fetchSessions();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.examSessions.closeError") });
    }
  };

  const handleCancel = async (session) => {
    try {
      await cancelExamSession(session.id);
      setFeedback({ type: "success", message: t("pages.examSessions.cancelSuccess") });
      fetchSessions();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.examSessions.cancelError") });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("menu.exams.sessions")}</li>
              </ol>
            </nav>
          </div>
          {canCreateSession && (
            <div className="mb-2">
              <button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.examSessions.planLabel")}
              </button>
            </div>
          )}
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.examSessions.title")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="mb-3 me-2">
                <select className="form-select" value={examFilter} onChange={(e) => { setExamFilter(e.target.value); setPage(1); }}>
                  <option value="">{t("common.ui.tousExamens")}</option>
                  {exams.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3 me-2">
                <select className="form-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                  <option value="">{t("common.crud.allStatuses")}</option>
                  <option value="scheduled">{t("common.examSessionStatus.scheduled")}</option>
                  <option value="ongoing">{t("common.examSessionStatus.ongoing")}</option>
                  <option value="closed">{t("common.examSessionStatus.closed")}</option>
                  <option value="cancelled">{t("common.examSessionStatus.cancelled")}</option>
                </select>
              </div>
            </div>
          </div>

          <DataTablePagination
            variant="header"
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={(v) => { setLimit(v); setPage(1); }}
          />

          <div className="card-body p-0 py-3">
            <div className="custom-datatable-filter table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.ui.examen")}</th>
                    <th>{t("common.fields.class")}</th>
                    <th>{t("common.ui.groupe")}</th>
                    <th>{t("common.ui.debut")}</th>
                    <th>{t("common.ui.fin")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : sessions.length > 0 ? (
                    sessions.map((session) => (
                      <tr key={session.id}>
                        <td className="fw-medium">{label(exams, session.exam_id)}</td>
                        <td>{session.class_id ? label(classes, session.class_id) : "-"}</td>
                        <td>{session.student_group_id ? label(studentGroups, session.student_group_id) : "-"}</td>
                        <td>{formatMauritaniaDateTime(session.start_at)}</td>
                        <td>{formatMauritaniaDateTime(session.end_at)}</td>
                        <td>
                          <span className={`badge badge-soft-${statusBadge[session.status] || "secondary"}`}>
                            {statusLabels[session.status] || session.status}
                          </span>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                            >
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              {canUpdateSession && (
                                <Dropdown.Item className="rounded-1" onClick={() => openEditModal(session)}>
                                  <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                                </Dropdown.Item>
                              )}
                              {session.status === "scheduled" && canOpenSession && (
                                <Dropdown.Item className="rounded-1" onClick={() => handleOpen(session)}>
                                  <i className="ti ti-player-play me-2"></i>{t("pages.examSessions.demarrer")}
                                </Dropdown.Item>
                              )}
                              {canUpdateSession && (
                                <Dropdown.Item className="rounded-1" onClick={() => openRoomsModal(session)}>
                                  <i className="ti ti-building me-2"></i>{t("pages.examSessions.salles")}
                                </Dropdown.Item>
                              )}
                              <Dropdown.Item
                                className="rounded-1"
                                as={Link}
                                to={`/attempts?exam_session_id=${session.id}&status=in_progress`}
                              >
                                <i className="ti ti-broadcast me-2"></i>{t("pages.examSessions.superviserDirect")}
                              </Dropdown.Item>
                              <Dropdown.Item className="rounded-1" onClick={() => handleDownloadAttendance(session)}>
                                <i className="ti ti-file-spreadsheet me-2"></i>{t("pages.examSessions.emargement")}
                              </Dropdown.Item>
                              {["scheduled", "ongoing"].includes(session.status) && canCloseSession && (
                                <Dropdown.Item className="rounded-1" onClick={() => handleClose(session)}>
                                  <i className="ti ti-lock me-2"></i>{t("pages.examSessions.closeAction")}
                                </Dropdown.Item>
                              )}
                              {["scheduled", "ongoing"].includes(session.status) && canCloseSession && (
                                <Dropdown.Item className="rounded-1" onClick={() => handleCancel(session)}>
                                  <i className="ti ti-ban me-2"></i>{t("common.crud.cancel")}
                                </Dropdown.Item>
                              )}
                              {canDeleteSession && (
                                <Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(session)}>
                                  <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.examSessions.noSessionsFound")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <DataTablePagination
              variant="footer"
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onLimitChange={(v) => { setLimit(v); setPage(1); }}
            />
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{formMode === "add" ? t("pages.examSessions.planLabel") : t("pages.examSessions.editTitle")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            <div className="mb-3">
              <label className="form-label">
                {t("common.ui.examen")} <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${formErrors.exam_id ? "is-invalid" : ""}`}
                value={formData.exam_id}
                onChange={(e) => handleFormChange("exam_id", e.target.value)}
              >
                <option value="">{t("common.crud.selectPlaceholder")}</option>
                {exams.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.title}
                  </option>
                ))}
              </select>
              {formErrors.exam_id && <div className="invalid-feedback d-block">{formErrors.exam_id}</div>}
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.examSessions.classOptional")}</label>
                <select className="form-select" value={formData.class_id} onChange={(e) => handleFormChange("class_id", e.target.value)}>
                  <option value="">{t("common.fields.noneFem")}</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.ui.groupeOptionnel")}</label>
                <select
                  className="form-select"
                  value={formData.student_group_id}
                  onChange={(e) => handleFormChange("student_group_id", e.target.value)}
                >
                  <option value="">{t("common.fields.noneMasc")}</option>
                  {studentGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("common.ui.debut")} <span className="text-danger">*</span>
                </label>
                <input
                  type="datetime-local"
                  className={`form-control ${formErrors.start_at ? "is-invalid" : ""}`}
                  value={formData.start_at}
                  onChange={(e) => handleFormChange("start_at", e.target.value)}
                />
                {formErrors.start_at && <div className="invalid-feedback">{formErrors.start_at}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("common.ui.fin")} <span className="text-danger">*</span>
                </label>
                <input
                  type="datetime-local"
                  className={`form-control ${formErrors.end_at ? "is-invalid" : ""}`}
                  value={formData.end_at}
                  onChange={(e) => handleFormChange("end_at", e.target.value)}
                />
                {formErrors.end_at && <div className="invalid-feedback">{formErrors.end_at}</div>}
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">{t("pages.examSessions.accessCodeOptional")}</label>
              <input
                type="text"
                className="form-control"
                value={formData.access_code}
                onChange={(e) => handleFormChange("access_code", e.target.value)}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : formMode === "add" ? t("pages.examSessions.plan") : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p>{t("pages.examSessions.confirmDeleteBody")}</p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.crud.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Salles affectées à la session ("nombre de salles et de surveillants") */}
      <Modal show={roomsModalSession !== null} onHide={() => setRoomsModalSession(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("pages.examSessions.sallesAffectees")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex mb-3">
            <select
              className="form-select me-2"
              value={selectedRoomToAdd}
              onChange={(e) => setSelectedRoomToAdd(e.target.value)}
            >
              <option value="">{t("pages.examSessions.choisirSalleAjouter")}</option>
              {allRooms
                .filter((r) => !sessionRooms.some((sr) => sr.room_id === r.id))
                .map((r) => (
                  <option key={r.id} value={r.id}>{r.code} - {r.name}</option>
                ))}
            </select>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!selectedRoomToAdd || roomsSubmitting}
              onClick={handleAddRoom}
            >
              {t("common.crud.add")}
            </button>
          </div>

          {roomsLoading ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t("common.crud.loading")}</span></div>
            </div>
          ) : sessionRooms.length > 0 ? (
            <ul className="list-group">
              {sessionRooms.map((sr) => (
                <li key={sr.room_id} className="list-group-item d-flex justify-content-between align-items-center">
                  <span>{sr.room_code} - {sr.room_name} <span className="text-muted">{t("pages.examSessions.capaciteExamen")} {sr.capacity_exam ?? "-"})</span></span>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveRoom(sr.room_id)}>
                    <i className="ti ti-x"></i>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted mb-0">{t("pages.examSessions.aucuneSalleAffecteePourInstantAffectation")}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={() => setRoomsModalSession(null)}>{t("common.crud.close")}</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ExamSessions;
